import { useEffect, useMemo, useRef, useState } from 'react';
import type { Contact, ContactTab, ImportResult, Salutation, Skipped } from '@schwarz/contract';
import {
  Salutation as SalutationEnum,
  callDispositionLabels,
  salutationLabels,
  skipReasonMessages,
} from '@schwarz/contract';

import {
  useAddToQueueMutation,
  useGetContactsQuery,
  usePlaceInstantCallsMutation,
  useGetQueueQuery,
  useImportContactsMutation,
  useRemoveFromQueueMutation,
  useScheduleCallsMutation,
  useSetSalutationMutation,
} from '../../store/contacts-api.js';
import { useAppDispatch } from '../../store/hooks.js';
import {
  IconCalendar,
  IconCheck,
  IconClock,
  IconPhone,
  IconPlus,
  IconSearch,
  IconUpload,
  IconX,
} from '../../ui/icons.js';
import { initials } from '../../ui/initials.js';
import { messageOf } from '../../store/error-message.js';
import { pushToast } from '../../ui/toast-store.js';
import {
  pollingInterval,
  resolutionToasts,
  takeSnapshot,
  type ActivitySnapshot,
} from './call-activity.js';
import { callMessage } from './call-message.js';
import { ImportDialog } from './ImportDialog.js';
import { PAGE_SIZES, Pagination } from '../../ui/Pagination.js';
import { when } from '../../ui/when.js';
import { ScheduleDialog } from './ScheduleDialog.js';
import styles from './ContactsPage.module.css';

/** How long to wait before turning keystrokes into a request. */
const SEARCH_DEBOUNCE_MS = 250;

/** Why a call cannot be placed to a suppressed contact. */

const DNC_REASON = 'On the do-not-call list';

/**
 * Why a contact cannot be rung again yet.
 *
 * Their last call is still ringing or still connected. A second one would ring
 * the same phone mid-conversation; the API refuses it too, this just says so
 * before anybody clicks.
 */
const IN_CALL_REASON = 'A call to this contact is still in progress';

function isOnCall(row: Contact): boolean {
  return row.lastOutcome === 'DIALING';
}

/**
 * Why a queued contact cannot also be scheduled.
 *
 * A queue entry means "call at the next opportunity" and a schedule means "call
 * at this time". Holding both would dial the contact twice, and the API has no
 * already-queued skip on the schedule path — a schedule can legitimately be one
 * of several — so this is the only thing standing between the two.
 */
const QUEUED_REASON = 'Already in the queue — take it out to schedule instead';

/**
 * Contacts & Queue (S05, S06).
 *
 * The table and the queue panel are one page because they are one workflow:
 * select rows on the left, act on them, watch the queue on the right. The mock
 * lays it out as a two-column grid and so does this.
 */
export function ContactsPage(): JSX.Element {
  const dispatch = useAppDispatch();

  const [tab, setTab] = useState<ContactTab>('waiting');
  const [typed, setTyped] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZES[0]);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());

  // Debounced, so a search does not fire a request per keystroke. The input is
  // driven by `typed` and the query by `search`, which is what keeps typing
  // smooth while the list catches up.
  useEffect(() => {
    const timer = window.setTimeout(() => setSearch(typed), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [typed]);

  // A narrowed search almost always has fewer pages than the old one, so page 7
  // of the previous result would land on an empty table.
  useEffect(() => {
    setPage(1);
    setSelected(new Set());
  }, [search]);

  const query = useMemo(
    () => ({
      tab,
      page,
      pageSize,
      ...(search.trim() ? { search: search.trim() } : {}),
    }),
    [tab, search, page, pageSize],
  );

  /**
   * How fast to re-read, or not at all.
   *
   * State rather than a derived value because it is read by the very queries
   * that produce the data it is derived from. An effect keeps it one render
   * behind, which is harmless: the first poll is due seconds later anyway.
   */
  const [poll, setPoll] = useState<number | undefined>(undefined);

  // Undefined rather than 0 when idle: RTK Query treats 0 as "no polling", but
  // passing it explicitly still tears down and rebuilds the subscription.
  const pollOption = poll === undefined ? {} : { pollingInterval: poll };

  const { data, isLoading, isError, refetch } = useGetContactsQuery(query, pollOption);
  // The queue is polled on the same terms: its entries turn "calling" and then
  // leave, and neither happens in response to anything the operator did.
  const { data: queue } = useGetQueueQuery(undefined, pollOption);

  useEffect(() => {
    setPoll(pollingInterval(data?.items ?? [], queue?.count ?? 0, queue?.callsInFlight ?? 0));
  }, [data, queue]);

  /**
   * The last set of rows we compared against, for spotting calls that finished.
   *
   * Reset whenever the query changes: switching tab, page or search changes
   * which rows are visible for reasons that have nothing to do with calls, and a
   * row that vanished because the operator paged away has not finished.
   */
  const watched = useRef<ActivitySnapshot>(new Map());
  useEffect(() => {
    watched.current = new Map();
  }, [query]);

  useEffect(() => {
    const rows = data?.items;
    if (!rows) return;
    for (const toast of resolutionToasts(watched.current, rows)) {
      dispatch(pushToast(toast.kind, toast.message));
    }
    watched.current = takeSnapshot(rows);
  }, [data, dispatch]);

  const [setSalutation] = useSetSalutationMutation();
  const [addToQueue, { isLoading: queueing }] = useAddToQueueMutation();
  const [placeInstantCalls, { isLoading: calling }] = usePlaceInstantCallsMutation();
  const [removeFromQueue] = useRemoveFromQueueMutation();
  const [scheduleCalls, { isLoading: scheduling }] = useScheduleCallsMutation();
  const [importContacts, { isLoading: importing }] = useImportContactsMutation();

  // The import dialog holds its own two states — pick a file, then read the
  // report — so the page only has to know whether it is open and what came
  // back. The error lives here rather than in a toast because it belongs beside
  // the file picker that has to be tried again.
  const [importOpen, setImportOpen] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [importError, setImportError] = useState<string | null>(null);

  // Scheduling can be aimed at the selection or at a single row, so the dialog
  // opens with its target rather than with a boolean. `fromSelection` decides
  // whether success clears the checkboxes: scheduling one row should not.
  const [scheduleFor, setScheduleFor] = useState<{
    contacts: Contact[];
    fromSelection: boolean;
  } | null>(null);

  const rows = data?.items ?? [];
  const counts = data?.counts ?? { waiting: 0, history: 0 };
  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  // Deleting or suppressing the last rows of the last page can leave the current
  // page past the end. Correcting it here rather than clamping on render means
  // the request that follows asks for a page that exists.
  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  // Only rows that can actually be queued are selectable, so the bulk action
  // cannot be assembled into a request the API is bound to refuse.
  const selectable = useMemo(() => rows.filter((row) => !row.doNotCall && !row.inQueue), [rows]);
  const chosen = useMemo(
    () => selectable.filter((row) => selected.has(row.id)),
    [selectable, selected],
  );
  const allChosen = selectable.length > 0 && chosen.length === selectable.length;

  /**
   * Of the selection, the ones that can actually be rung.
   *
   * Not `chosen`: that excludes contacts already in the queue, which is right
   * for queueing and wrong for calling. A queued contact means "call at the next
   * opportunity", and an operator asking for a call now is asking for exactly
   * that opportunity. Only the do-not-call flag and a call already in progress
   * rule a contact out.
   */
  const callable = useMemo(
    () => rows.filter((row) => selected.has(row.id) && !row.doNotCall && !isOnCall(row)),
    [rows, selected],
  );

  // Selection is per-tab: the rows are different, and carrying ids across would
  // silently act on something the operator can no longer see.
  function switchTab(next: ContactTab): void {
    setTab(next);
    setPage(1);
    setSelected(new Set());
  }

  /**
   * Selection is per page, as it is per tab.
   *
   * The header checkbox can only speak for the rows that are loaded, so a
   * selection carried across a page change would be one the operator cannot see
   * or review before acting on it — and "Add to queue" acting on 40 invisible
   * contacts is exactly the kind of bulk action nobody wants to explain.
   */
  function goToPage(next: number): void {
    setPage(Math.min(Math.max(1, next), pageCount));
    setSelected(new Set());
  }

  function toggleRow(id: string): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  /**
   * Call these contacts now (S07).
   *
   * Takes the rows rather than reading the selection, because the same handler
   * serves the bulk button and each row button. The selection is only cleared
   * when it was the selection that was acted on.
   */
  async function onInstantCall(rows: readonly Contact[], fromSelection: boolean): Promise<void> {
    const contactIds = rows.map((row) => row.id);
    if (contactIds.length === 0) return;

    try {
      const result = await placeInstantCalls({ contactIds }).unwrap();
      // Only on a placed call. Clearing after an all-refused batch would take
      // away the selection the operator needs to act on the reason given.
      if (fromSelection && result.placed > 0) setSelected(new Set());
      const { kind, message } = callMessage(result);
      dispatch(pushToast(kind, message));
    } catch (error) {
      dispatch(pushToast('error', messageOf(error)));
    }
  }

  async function onAddToQueue(): Promise<void> {
    const ids = chosen.map((row) => row.id);
    if (ids.length === 0) return;

    try {
      const result = await addToQueue({ contactIds: ids }).unwrap();
      setSelected(new Set());
      dispatch(
        pushToast(
          'success',
          bulkMessage(result.added, result.skipped, {
            one: 'contact added to the queue',
            many: 'contacts added to the queue',
          }),
        ),
      );
    } catch (error) {
      dispatch(pushToast('error', messageOf(error)));
    }
  }

  async function onSchedule(input: { scheduledFor: string; timezone: string }): Promise<void> {
    if (!scheduleFor) return;
    const ids = scheduleFor.contacts.map((row) => row.id);
    if (ids.length === 0) return;

    try {
      const result = await scheduleCalls({
        contactIds: ids,
        scheduledFor: input.scheduledFor,
        timezone: input.timezone,
      }).unwrap();
      if (scheduleFor.fromSelection) setSelected(new Set());
      setScheduleFor(null);
      dispatch(
        pushToast(
          'success',
          bulkMessage(result.scheduled, result.skipped, {
            one: 'contact scheduled',
            many: 'contacts scheduled',
          }),
        ),
      );
    } catch (error) {
      dispatch(pushToast('error', messageOf(error)));
    }
  }

  async function onImport(file: File): Promise<void> {
    setImportError(null);
    try {
      const result = await importContacts(file).unwrap();
      setImportResult(result);
      // The list is a different length now, and the operator wants to see what
      // arrived rather than whichever page they were on.
      setPage(1);
      // The selection was made against the old list; some of those rows may
      // have just been rewritten.
      setSelected(new Set());
    } catch (error) {
      setImportError(messageOf(error));
    }
  }

  function closeImport(): void {
    setImportOpen(false);
    setImportError(null);

    // The toast is the record that survives closing the dialog; the counts it
    // reports are the ones the operator just read.
    if (importResult) {
      const { created, updated, rejected } = importResult;
      const parts = [];
      if (created > 0) parts.push(`${created} contact${created === 1 ? '' : 's'} added`);
      if (updated > 0) parts.push(`${updated} updated`);
      if (rejected.length > 0) parts.push(`${rejected.length} rejected`);
      dispatch(
        pushToast(
          rejected.length > 0 && created + updated === 0 ? 'error' : 'success',
          parts.length > 0 ? parts.join(', ') : 'Nothing to import',
        ),
      );
    }
    setImportResult(null);
  }

  async function onSalutationChange(contact: Contact, salutation: Salutation): Promise<void> {
    try {
      await setSalutation({ id: contact.id, salutation, query }).unwrap();
    } catch (error) {
      // The optimistic patch is already rolled back by the endpoint; this only
      // has to explain why the value moved back.
      dispatch(pushToast('error', messageOf(error)));
    }
  }

  async function onRemoveFromQueue(id: string): Promise<void> {
    try {
      await removeFromQueue(id).unwrap();
    } catch (error) {
      dispatch(pushToast('error', messageOf(error)));
    }
  }

  const isWaiting = tab === 'waiting';
  const columns = isWaiting ? 7 : 6;

  return (
    <section className={styles.page}>
      <div className={styles.grid}>
        <div className={styles.main}>
          <div className={styles.toolbar}>
            <div className={styles.search}>
              <IconSearch />
              <input
                value={typed}
                onChange={(event) => setTyped(event.target.value)}
                placeholder="Search name, phone, company or email…"
                aria-label="Search contacts"
              />
            </div>

            <button
              type="button"
              className={styles.btn}
              onClick={() => setImportOpen(true)}
              title="Add contacts from a CSV file"
            >
              <IconUpload width="15" height="15" />
              Import CSV
            </button>
          </div>

          <div className={styles.tabs} role="tablist" aria-label="Calling list">
            <button
              type="button"
              role="tab"
              aria-selected={isWaiting}
              className={`${styles.tab} ${isWaiting ? styles.tabActive : ''}`}
              onClick={() => switchTab('waiting')}
            >
              Waiting to call
              <span className={styles.tabCount}>{counts.waiting}</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={!isWaiting}
              className={`${styles.tab} ${!isWaiting ? styles.tabActive : ''}`}
              onClick={() => switchTab('history')}
            >
              Call history
              <span className={styles.tabCount}>{counts.history}</span>
            </button>
          </div>

          {chosen.length > 0 && (
            <div className={styles.selbar}>
              <span>
                <b>{chosen.length}</b> selected
              </span>
              <button type="button" className={styles.clear} onClick={() => setSelected(new Set())}>
                Clear
              </button>
              <button
                type="button"
                className={styles.btnSm}
                disabled={queueing}
                onClick={() => void onAddToQueue()}
              >
                <IconPlus width="14" height="14" />
                Add to queue
              </button>
              <button
                type="button"
                className={`${styles.btnSm} ${styles.btnCall}`}
                disabled={calling || callable.length === 0}
                title={
                  callable.length === 0
                    ? 'Every selected contact is on the do-not-call list or already on a call'
                    : `Call ${callable.length === 1 ? 'this contact' : `these ${callable.length} contacts`} now`
                }
                onClick={() => void onInstantCall(callable, true)}
              >
                <IconPhone width="14" height="14" />
                Instant call
              </button>
              <button
                type="button"
                className={styles.btnPrimarySm}
                onClick={() => setScheduleFor({ contacts: chosen, fromSelection: true })}
              >
                <IconCalendar width="14" height="14" />
                Schedule call
              </button>
            </div>
          )}

          <div className={styles.panel}>
            <div className={styles.tableWrap}>
              <table>
                <thead>
                  <tr>
                    {isWaiting && (
                      <th className={styles.pick}>
                        <input
                          type="checkbox"
                          checked={allChosen}
                          ref={(node) => {
                            if (node) node.indeterminate = chosen.length > 0 && !allChosen;
                          }}
                          disabled={selectable.length === 0}
                          onChange={(event) =>
                            setSelected(
                              event.target.checked
                                ? new Set(selectable.map((row) => row.id))
                                : new Set(),
                            )
                          }
                          aria-label="Select all queueable contacts"
                        />
                      </th>
                    )}
                    <th>Contact</th>
                    <th>Phone</th>
                    <th>Email</th>
                    <th>Greeting</th>
                    <th>Call</th>
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {isLoading && (
                    <tr>
                      <td colSpan={columns} className={styles.loading}>
                        Loading contacts…
                      </td>
                    </tr>
                  )}

                  {isError && (
                    <tr>
                      <td colSpan={columns}>
                        <div className={styles.empty}>
                          <p>Could not load contacts.</p>
                          <button
                            type="button"
                            className={styles.btn}
                            onClick={() => void refetch()}
                          >
                            Try again
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}

                  {data && rows.length === 0 && (
                    <tr>
                      <td colSpan={columns}>
                        <div className={styles.empty}>
                          <div className={styles.emptyIcon}>
                            <IconPhone />
                          </div>
                          <h3>{emptyTitle(tab, search)}</h3>
                          <p>{emptyBody(tab, search)}</p>
                        </div>
                      </td>
                    </tr>
                  )}

                  {rows.map((row) => (
                    <tr key={row.id} className={selected.has(row.id) ? styles.chosen : undefined}>
                      {isWaiting && (
                        <td className={styles.pick}>
                          <input
                            type="checkbox"
                            checked={selected.has(row.id)}
                            disabled={row.doNotCall || row.inQueue}
                            onChange={() => toggleRow(row.id)}
                            aria-label={`Select ${row.name}`}
                            title={
                              row.doNotCall ? DNC_REASON : row.inQueue ? QUEUED_REASON : undefined
                            }
                          />
                        </td>
                      )}

                      <td>
                        <div className={styles.nameCell}>
                          <div className={styles.av} aria-hidden="true">
                            {initials(row.name)}
                          </div>
                          <div className={styles.who}>
                            <b>{row.name}</b>
                            <small>{row.company ?? '—'}</small>
                          </div>
                        </div>
                      </td>

                      <td>
                        <span className={styles.phone}>{formatPhone(row.phone)}</span>
                      </td>

                      <td>
                        {row.email ? (
                          <a className={styles.email} href={`mailto:${row.email}`}>
                            {row.email}
                          </a>
                        ) : (
                          <span className={styles.emailNone}>—</span>
                        )}
                      </td>

                      <td>
                        <select
                          className={styles.greet}
                          value={row.salutation}
                          title="How the agent should address this contact"
                          aria-label={`Greeting for ${row.name}`}
                          onChange={(event) =>
                            void onSalutationChange(row, event.target.value as Salutation)
                          }
                        >
                          {SalutationEnum.values.map((value) => (
                            <option key={value} value={value}>
                              {salutationLabels[value]}
                            </option>
                          ))}
                        </select>
                      </td>

                      <td>
                        <CallBadge contact={row} />
                        {row.lastCalledAt && (
                          <div className={styles.callWhen}>{when(row.lastCalledAt)}</div>
                        )}
                      </td>

                      <td className={styles.actions}>
                        <div className={styles.rowActs}>
                          {row.inQueue && <span className={styles.queuedTag}>queued</span>}
                          {isWaiting && row.doNotCall && (
                            <span className={styles.dncTag}>do not call</span>
                          )}

                          {/* Two shapes for one action, following the mock.
                           *
                           * In the waiting list this is one of several icon
                           * buttons and stays an icon. In the history list it
                           * is the only thing an operator is likely to want,
                           * and only for a call nobody answered — so it is
                           * labelled, and absent entirely for a call that was
                           * answered or that failed. Offering "call again" on
                           * an answered call invites ringing somebody who has
                           * already been spoken to. */}
                          {isWaiting ? (
                            <button
                              type="button"
                              className={`${styles.rowAct} ${styles.rowActCall}`}
                              disabled={row.doNotCall || isOnCall(row) || calling}
                              title={
                                row.doNotCall
                                  ? DNC_REASON
                                  : isOnCall(row)
                                    ? IN_CALL_REASON
                                    : 'Call now'
                              }
                              aria-label={`Instant call: ${row.name}`}
                              onClick={() => void onInstantCall([row], false)}
                            >
                              <IconPhone width="16" height="16" />
                            </button>
                          ) : (
                            row.lastOutcome === 'MISSED' && (
                              <button
                                type="button"
                                className={`${styles.btnSm} ${styles.btnCall}`}
                                disabled={row.doNotCall || calling}
                                title={row.doNotCall ? DNC_REASON : 'Call again'}
                                aria-label={`Call again: ${row.name}`}
                                onClick={() => void onInstantCall([row], false)}
                              >
                                <IconPhone width="14" height="14" />
                                Call again
                              </button>
                            )
                          )}

                          {/* No schedule button in the history tab: scheduling a
                              follow-up from a finished call is deliberately out of
                              scope. */}
                          {isWaiting && (
                            <button
                              type="button"
                              className={styles.rowAct}
                              disabled={row.doNotCall || row.inQueue}
                              title={
                                row.doNotCall
                                  ? DNC_REASON
                                  : row.inQueue
                                    ? QUEUED_REASON
                                    : 'Schedule a call'
                              }
                              aria-label={`Schedule a call for ${row.name}`}
                              onClick={() =>
                                setScheduleFor({ contacts: [row], fromSelection: false })
                              }
                            >
                              <IconCalendar width="16" height="16" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {total > 0 && (
              <Pagination
                page={page}
                pageSize={pageSize}
                total={total}
                onPage={goToPage}
                onPageSize={setPageSize}
              />
            )}
          </div>
        </div>

        <div className={styles.panel}>
          <div className={styles.panelHead}>
            <h3>Call queue</h3>
            <span className={styles.pillCount}>{queue?.count ?? 0}</span>
          </div>

          {(queue?.items.length ?? 0) === 0 ? (
            <div className={styles.empty}>
              <div className={styles.emptyIcon}>
                <IconClock />
              </div>
              <h3>Queue is empty</h3>
              <p>Select contacts and add them, or schedule a call.</p>
            </div>
          ) : (
            <div>
              {queue?.items.map((entry) => (
                <div className={styles.qItem} key={entry.id}>
                  <div className={styles.av} aria-hidden="true">
                    {initials(entry.contact.name)}
                  </div>
                  <div className={styles.qt}>
                    <b>{entry.contact.name}</b>
                    <small>
                      {/* An entry being dialled says so, rather than still
                          advertising when it was due. It leaves the queue of its
                          own accord once the call resolves. */}
                      {entry.status === 'DIALING' ? (
                        <span className={styles.qCalling}>
                          {entry.inCall ? 'Call in progress' : 'Calling…'}
                        </span>
                      ) : (
                        <>
                          <IconClock />
                          {/* The mock puts the offer here. With offers gone from the
                              build, the company is what is left that an operator
                              glancing at the queue actually needs. */}
                          {entry.targetTime ? when(entry.targetTime) : 'Next available'}
                        </>
                      )}
                      {entry.contact.company ? ' · ' + entry.contact.company : ''}
                    </small>
                  </div>
                  <button
                    type="button"
                    className={styles.qx}
                    disabled={entry.status === 'DIALING'}
                    onClick={() => void onRemoveFromQueue(entry.id)}
                    aria-label={`Take ${entry.contact.name} out of the queue`}
                    title={
                      entry.status === 'DIALING'
                        ? 'The call is already going out'
                        : 'Take out of the queue'
                    }
                  >
                    <IconX width="15" height="15" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <ImportDialog
        open={importOpen}
        busy={importing}
        result={importResult}
        error={importError}
        onCancel={closeImport}
        onImport={(file) => void onImport(file)}
        onDone={closeImport}
      />

      <ScheduleDialog
        open={scheduleFor !== null}
        contacts={scheduleFor?.contacts ?? []}
        busy={scheduling}
        onCancel={() => setScheduleFor(null)}
        onConfirm={(input) => void onSchedule(input)}
      />
    </section>
  );
}

/** The outcome chip in the Call column. */
function CallBadge({ contact }: { contact: Contact }): JSX.Element {
  if (contact.lastOutcome === 'DIALING') {
    // Same chip either way: the call is live in both. Only the words change,
    // the moment the platform says the contact picked up.
    return (
      <span className={`${styles.badge} ${styles.calling}`}>
        <span className={styles.dot} />
        {contact.inCall ? 'Call in progress' : 'Calling…'}
      </span>
    );
  }
  if (contact.lastOutcome === 'ANSWERED') {
    return (
      <span className={`${styles.badge} ${styles.answered}`}>
        <IconCheck width="13" height="13" />
        Answered
      </span>
    );
  }
  if (contact.lastOutcome === 'MISSED' || contact.lastOutcome === 'FAILED') {
    const failed = contact.lastOutcome === 'FAILED';
    // "No answer" and "Busy / declined" are the same outcome and different
    // news; a platform stop is a failure nobody should read as a bad number.
    // Calls resolved before the platform reported a disposition have none.
    const detail =
      contact.lastDisposition === 'NO_ANSWER' ||
      contact.lastDisposition === 'BUSY' ||
      contact.lastDisposition === 'PLATFORM_TERMINATED'
        ? callDispositionLabels[contact.lastDisposition]
        : null;
    return (
      <span
        className={`${styles.badge} ${styles.missed} ${failed && contact.lastFailureReason ? styles.explained : ''}`}
        // "Failed" alone reads as "this number is bad". The reason is almost
        // always that we never heard back about the call, which is a different
        // thing entirely and the operator should be able to see which.
        title={
          failed
            ? (contact.lastFailureReason ??
              'The call produced no result, and no reason was recorded.')
            : contact.lastDisposition === 'BUSY'
              ? 'The line was busy, or the contact declined the call'
              : 'Nobody answered'
        }
      >
        <IconX width="13" height="13" />
        {failed ? 'Failed' : 'Missed'}
        {detail !== null && ` · ${detail}`}
      </span>
    );
  }
  return (
    <span className={`${styles.badge} ${styles.awaiting}`}>
      <span className={styles.dot} />
      Not called
    </span>
  );
}

/**
 * "3 contacts added to the queue. Iva Hristova is on the do-not-call list."
 *
 * Naming the first skipped contact is the point — a bare count leaves the
 * operator to work out which of eleven selected rows did not make it. Shared by
 * both bulk actions so their wording cannot drift apart.
 */
function bulkMessage(
  count: number,
  skipped: Skipped[],
  words: { one: string; many: string },
): string {
  const head = `${count} ${count === 1 ? words.one : words.many}`;
  if (skipped.length === 0) return head;

  const first = skipped[0];
  const detail = first
    ? `${first.name ?? 'One contact'} is ${skipReasonMessages[first.reason]}`
    : '';
  const rest = skipped.length > 1 ? ` and ${skipped.length - 1} more skipped` : '';
  return `${head}. ${detail}${rest}.`;
}

/**
 * E.164 back into something readable.
 *
 * Stored canonical and displayed grouped, because +359884129920 is a string and
 * +359 88 412 9920 is a phone number. Bulgarian grouping, since that is the
 * market; anything else falls back to the raw value rather than being mangled.
 */
function formatPhone(phone: string): string {
  const bg = /^\+359(\d{2})(\d{3})(\d{4})$/.exec(phone);
  if (bg) return `+359 ${bg[1]} ${bg[2]} ${bg[3]}`;
  return phone;
}

/** "Today 11:04" / "Yesterday 16:12" / "14 Aug 09:30", as the mock words it. */

function emptyTitle(tab: ContactTab, search: string): string {
  if (search) return 'Nothing matches';
  return tab === 'waiting' ? 'No contacts waiting' : 'No calls yet';
}

function emptyBody(tab: ContactTab, search: string): string {
  if (search) return 'Try a different search.';
  return tab === 'waiting'
    ? 'Import a CSV or add a contact to build a calling list.'
    : 'Once a call is answered or missed, the contact moves here.';
}
